import { z } from "zod";
import {
  CONSENT_TYPES,
  type ConsentType,
} from "./consent";

/**
 * UC-006 Mikroskupina onboarding 5-step Wizard schema (Phase 28 / Milestone 20.3).
 *
 * Krok 1: Set password (NIST SP 800-63B — min 14 znaků, žádné komplexitní
 *         pravidla per OWASP ASVS V2).
 * Kroky 2-5: 4-step consent reuse z Phase 4 (Read → Acknowledge → Accept →
 *            Success). Consent state je gathered v ConsentStepper komponentě,
 *            tento schema validuje final aggregated payload posílaný backendu.
 *
 * Backend endpoint: `POST /api/public/microgroup/onboarding/{token}/complete`
 * z Phase 19. Token je ConsumedToken `microgroup_onboarding` (14d TTL, R-039
 * 1-time-use enforce).
 */

export const microgroupOnboardingPasswordSchema = z
  .object({
    password: z
      .string()
      .min(14, "Heslo musí mít alespoň 14 znaků (NIST SP 800-63B)."),
    passwordConfirm: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.password !== val.passwordConfirm) {
      ctx.addIssue({
        code: "custom",
        path: ["passwordConfirm"],
        message: "Hesla se neshodují.",
      });
    }
  });

export type MicrogroupOnboardingPasswordInput = z.infer<
  typeof microgroupOnboardingPasswordSchema
>;

/**
 * Final payload posílaný backendu po úspěšném dokončení všech 5 kroků.
 */
export const microgroupOnboardingCompletePayloadSchema = z.object({
  password: z.string().min(14, "Heslo musí mít alespoň 14 znaků."),
  consent_text_version: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}(_[a-z0-9]+)?$/,
      "consent_text_version musí být ISO formát.",
    ),
  consent_types: z.array(z.enum(CONSENT_TYPES)).nonempty(),
});

export type MicrogroupOnboardingCompletePayload = z.infer<
  typeof microgroupOnboardingCompletePayloadSchema
>;

/**
 * Token landing — backend vrací member metadata před consume.
 */
export interface MicrogroupOnboardingTokenInfo {
  /** Microgroup invitation id (UUID). */
  invitation_id: string;
  /** Display jméno member (Zdeněk) — UI greeting. */
  invited_name: string;
  /** E-mail member. */
  invited_email: string;
  /** Microgroup name (Milan's microgroup). */
  microgroup_name: string;
  /** Aktuální material consent_text_version k accept. */
  consent_text_version: string;
  /** HTML obsah consent textu (server-rendered markdown). */
  consent_text_html: string;
  /** Expirační timestamp tokenu (ISO). */
  expires_at: string;
}

export const DEFAULT_ONBOARDING_CONSENT_TYPES: ConsentType[] = [
  "data_processing",
  "data_sharing_axigon",
];
